
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { MapPin, Server, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { useNodeSelection } from "@/contexts/node-selection";

type Provisioner = {
  id: string;
  address: string;
};

export function NodeSelector() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { selectedNode, setSelectedNode } = useNodeSelection();

  const { data: nodes = [] } = useQuery({
    queryKey: ["provisioners", search],
    queryFn: async () => {
      if (!search.trim()) return [];
      
      const { data } = await supabase
        .from("provisioners")
        .select("id, address")
        .ilike("address", `%${search.trim()}%`)
        .limit(10);

      return (data || []) as Provisioner[];
    },
    enabled: open && search.length > 0,
  });

  const saveNode = async (node: Provisioner | null) => {
    if (node === null) {
      localStorage.removeItem('selectedNode');
      setSelectedNode(null);
      toast.success("Node preference removed");
      return;
    }

    localStorage.setItem('selectedNode', JSON.stringify(node));
    setSelectedNode(node);
    setOpen(false);
    setSearch("");
    toast.success("Node preference saved");
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    saveNode(null);
  };

  const formatAddress = (address: string) => {
    if (address.length <= 20) return address;
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[300px] justify-between"
        >
          {selectedNode ? (
            <div className="flex items-center gap-2 w-full">
              <Server className="h-4 w-4 shrink-0 text-dusk" />
              <span className="truncate">{formatAddress(selectedNode.address)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-auto hover:bg-destructive/20"
                onClick={clearSelection}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <>
              <span>Select a node...</span>
              <Server className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput
            placeholder="Search nodes..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {search.length === 0 ? "Type to search nodes..." : "No nodes found"}
            </CommandEmpty>
            <CommandGroup>
              {nodes.map((node) => (
                <CommandItem
                  key={node.id}
                  value={node.address}
                  onSelect={() => saveNode(node)}
                  className="cursor-pointer"
                >
                  <MapPin className="mr-2 h-4 w-4 text-dusk" />
                  <span className="truncate">{formatAddress(node.address)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
